import useSWR from "swr";

import * as fetcher from "../fetcher";
import { type Customer } from "./types";

interface CompanySearchItem {
  companyId: string;
  companyName: string;
}
interface CustomerSearchItem {
  customerId: string;
  companyId?: string;
}
interface CustomerIdentifierSearchResponse {
  customers?: CustomerSearchItem[];
  companies?: CompanySearchItem[];
  totalElements?: number;
  response?: string;
}

// This function generates a random number to be used as an external trace ID.
// Reference: https://github.com/ingka-group-digital/sp-saljapro-customer-profile/blob/main/src/utils/common.js
export const generateXFlowId = () => {
  const crypto = window.crypto || (window as any).msCrypto;
  const array = new Uint32Array(1);
  let id;
  if (crypto.getRandomValues) {
    id = crypto.getRandomValues(array);
  } else {
    id = (crypto as any).randomBytes?.(8);
  }
  return id?.[0];
};

export const customerIdentifierSearch = async (
  countryCode: string,
  customerType?: Customer["customerType"],
  email?: string
) => {
  if (!email) {
    return [];
  }

  const isBusiness = customerType === "Business";

  const endpoint = isBusiness ? "/business/v2/company" : "/private/v2/search";

  const payload = {
    identifier: email,
    ...(isBusiness ? {} : { externalTraceId: generateXFlowId() }),
  };

  const response = await fetcher.saljaCustomer.post<
    CustomerIdentifierSearchResponse,
    false
  >(endpoint, payload, {
    "country-code": countryCode,
    "the-timezone-iana": "Europe/Copenhagen", // Required header. Hard-coded value does not affect the result
  });

  if (isBusiness) {
    const companies = response.companies ?? [];
    return companies.map((c) => c.companyId);
  }

  const customers = response.customers ?? [];
  return customers.map((c) => c.customerId);
};

export function useCustomerIdentifierSearch(
  countryCode: string,
  customerType?: Customer["customerType"],
  email?: string
) {
  return useSWR<string[]>(
    email
      ? `customer-identifier-search/${countryCode}/${customerType}/${email}`
      : null,
    () => customerIdentifierSearch(countryCode, customerType, email),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
}
